import { ApplicationState } from './application-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';

import {
  Action,
  updateResourceByObjectKey,
  updateStatusById,
  updateStatus,
  setStatusDirtyById,
  StateStatus,
  updateResourceByParam,
  setStateDirtyByParam,
  setStatusByParam,
} from './utils/state-manager';
import { REJECTED_STATUS } from '../config/application-status-config';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: ApplicationState = {
  applicationsById: {},
  applicationsStateById: {},
  applicationsStateByJobIdAndStatus: {},
  applicationsStateByOrgIdAndStatus: {},
  applicationsStateByAccountIdAndStatus: {},
  applicationDetailsById: {},
  applicationDetailsStateById: {},
  labelsByJobApplicantId: {},
  labelStatusByJobId: {},
  statusChangeReasons: null,
  statusChangeReasonsStatus: null,
  applicationActivityById: {},
  applicationActivityByIdStatus: {},
  applicationCountsByJobId: {},
  applicationCountsByOrgId: {},
  applicationCountsStateByJobId: {},
  applicationCountsStateByOrgId: {},
  applicantFinalizingHireStepsByJobApplicantId: {},
  applicantFinalizingHireStepsStateByJobId: {},
  applicantFinalizingHireStepsStateByOrgId: {},
};

const setApplicationFields = (
  state: ApplicationState,
  jobApplicantId: number,
  fields: Record<string, any>,
) => {
  const newState = { ...state };
  const existingApplication = newState.applicationsById[jobApplicantId];
  newState.applicationsById = {
    ...newState.applicationsById,
    [jobApplicantId]: { ...existingApplication, ...fields },
  };
  return newState;
};

const updateApplicationByParamAndStatuses = (
  statuses: Array<ApplicationStatus>,
  state: any,
  resource: string,
  paramKey: string,
  paramValue: string,
  loading: boolean,
  data?: any,
) => {
  let newState = { ...state };
  statuses.forEach((s: ApplicationStatus) => {
    newState = updateResourceByParam(
      newState,
      resource,
      paramKey,
      `${paramValue}${s}`,
      loading,
      data,
    );
  });
  return newState;
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_APPLICATION'): {
      const { jobApplicantId } = action.payload.args;
      return updateResourceByParam(state, 'applications', 'id', jobApplicantId, true);
    }
    case SUCCESS('LOAD_APPLICATION'): {
      const {
        args: { jobApplicantId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'applications', 'id', jobApplicantId, false, {
        [jobApplicantId]: data,
      });
    }
    case FAILURE('LOAD_APPLICATION'): {
      const { jobApplicantId } = action.payload.args;
      return updateResourceByParam(state, 'applications', 'id', jobApplicantId, false);
    }
    case 'SET_APPLICATION_DIRTY': {
      const { jobApplicantId } = action.payload.args;
      return setStateDirtyByParam(state, 'applications', 'id', jobApplicantId);
    }
    case SUCCESS('MOVE_JOB_APPLICANT'): {
      const {
        args: { jobApplicantId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'applications', 'id', jobApplicantId, false, {
        [jobApplicantId]: data,
      });
    }
    case SUCCESS('APPLICATION_UPDATED'): {
      const { jobApplicantId } = action.payload.args;
      const jobId = state.applicationsById[jobApplicantId].job_id;
      const newState = {
        ...state,
        labelStatusByJobId: {
          ...state.labelStatusByJobId,
          [jobId]: { ...state.labelStatusByJobId[jobId], dirty: true },
        },
      };
      return setStateDirtyByParam(newState, 'applications', 'id', jobApplicantId);
    }
    case STARTING('LOAD_APPLICATION_DETAIL'): {
      const { jobApplicantId } = action.payload.args;
      return updateResourceByParam(state, 'applicationDetails', 'id', jobApplicantId, true);
    }
    case SUCCESS('LOAD_APPLICATION_DETAIL'): {
      const {
        args: { jobApplicantId },
        data,
      } = action.payload;
      const newState = { ...state };

      if (data.locations && data.locations.labels) {
        const map = convertArrayToMap(data.locations.labels, (a: JobApplicantLabelResponse) =>
          a.job_applicant_id.toString(),
        );
        newState.labelsByJobApplicantId = { ...state.labelsByJobApplicantId, ...map };
      }

      return updateResourceByParam(newState, 'applicationDetails', 'id', jobApplicantId, false, {
        [jobApplicantId]: data,
      });
    }
    case FAILURE('LOAD_APPLICATION_DETAIL'): {
      const { jobApplicantId } = action.payload.args;
      return updateResourceByParam(state, 'applicationDetails', 'id', jobApplicantId, false);
    }
    /**
     * DL - 2/6/2020 - in order to make the UI snappier when there's a slow connection,
     * the application status change happens as soon as we send it to the server. (This
     * set of changes was actually mostly cosmetic - we weren't relying on any information
     * from the server anyways)
     *
     * The only weirdness that occurs is that in the need-review.tsx component, we have to
     * check for both applicant status and the review todo. This arises because there is a
     * window where the status has been changed to 'ACCEPTED' but we haven't reloaded
     * to-do's yet.
     */
    case STARTING('APPLICATION_STATUS'): {
      const { jobApplicantId, statusChange } = action.payload.args;
      const conditionalFieldsToClear: Record<string, any> = {};

      if (!state.applicationsById[jobApplicantId]) {
        return state;
      }

      // if we are specifically rejecting someone, we want to
      // clear out their upcoming interviews too
      if (statusChange.status === REJECTED_STATUS) {
        conditionalFieldsToClear.interviewStartAt = null;
        conditionalFieldsToClear.interviewEndAt = null;
        conditionalFieldsToClear.interviewType = null;
      }

      return updateResourceByParam(state, 'applications', 'id', jobApplicantId, false, {
        [jobApplicantId]: {
          ...state.applicationsById[jobApplicantId],
          status: statusChange.status,
          expirationDate: null,
          ...conditionalFieldsToClear,
        },
      });
    }
    /**
     * The only thing we want to do is after a status is successfully updated, reload the application status to show in the activity feed
     * and reload application details to show in the applicant detail dialogs.
     */
    case SUCCESS('APPLICATION_STATUS'): {
      const { jobApplicantId, jobId, orgId } = action.payload.args;
      let newState = setStatusDirtyById(state, 'applicationActivityById', jobApplicantId);
      newState = setStateDirtyByParam(newState, 'applicationDetails', 'id', jobApplicantId);
      newState = setStateDirtyByParam(newState, 'applicationCounts', 'jobId', jobId);
      newState = setStateDirtyByParam(newState, 'applicationCounts', 'orgId', orgId);
      newState = setStateDirtyByParam(newState, 'applicationFinalizingHireSteps', 'orgId', orgId);
      newState = setStateDirtyByParam(newState, 'applicationFinalizingHireSteps', 'jobId', jobId);
      return newState;
    }
    case STARTING('STATUS_CHANGE_REASONS'): {
      return updateStatus(state, 'statusChangeReasons', true);
    }
    case SUCCESS('STATUS_CHANGE_REASONS'): {
      const { data } = action.payload;
      return updateStatus(state, 'statusChangeReasons', false, data);
    }
    case FAILURE('STATUS_CHANGE_REASONS'): {
      return updateStatus(state, 'statusChangeReasons', false);
    }
    case SUCCESS('SEND_TEXT_MESSAGE'): {
      let newState = { ...state };
      const { contactId, context } = action.payload;
      const { jobApplicantId } = context;
      if (!jobApplicantId) {
        return state;
      }

      const existingApplication = newState.applicationsById[jobApplicantId];
      newState.applicationsById = {
        ...newState.applicationsById,
        [jobApplicantId]: {
          ...existingApplication,
          expirationDate: null,
        },
      };

      // With org-level job phone numbers, we need to set the application activity dirty for every job applicant that shares
      // a contact. This is a little aggressive, as not every job is using the org level number, but it's fine to just reload
      // all applicants for a contact every time.
      Object.values(newState.applicationsById).forEach((application) => {
        if (application.contactId === contactId) {
          newState = setStatusDirtyById(
            newState,
            'applicationActivityById',
            String(application.job_applicant_id),
          );
        }
      });
      return newState;
    }
    /*
    DP - 3/1/2019
    TODO - Split out contacts into their own reducer
    Right now, we have "people" fields tacked onto the big applicant query.
    We should load people and represent them separately now and then use
    something like a memoized selector to "join" applicants and people. That
    way we would have one central representation we could use across
    different types of contacts for when we have things like an invalid
    phone number.
     */
    case 'INVALID_PHONE_NUMBER': {
      const { context } = action.payload;
      const { jobApplicantId } = context;
      if (!jobApplicantId) {
        return state;
      }
      return setApplicationFields(state, jobApplicantId, {
        isPhoneNumberInvalid: true,
      });
    }
    case SUCCESS('SET_INTERVIEW'): {
      const {
        args: { jobApplicantId },
        data: { interview },
      } = action.payload;

      if (interview.cancelledAt) {
        return setApplicationFields(state, jobApplicantId, {
          interviewStartAt: null,
          interviewEndAt: null,
          interviewType: null,
          interviewConfirmed: null,
        });
      }
      return setApplicationFields(state, jobApplicantId, {
        interviewStartAt: interview.startAt,
        interviewEndAt: interview.endAt,
        interviewType: interview.interviewType,
        interviewConfirmed: interview.isConfirmed,
      });
    }
    case SUCCESS('SET_HIRE_DATE'): {
      return setApplicationFields(state, action.payload.args.jobApplicantId, {
        hireStartDateTime: action.payload.args.hireStartDateTime
          ? action.payload.args.hireStartDateTime.format()
          : null,
      });
    }
    case 'IGNORE_APPLICATION_SUCCESS': {
      const newState = { ...state };
      newState.applicationsById = { ...newState.applicationsById };
      // Remove the application since we never even load ignored applications
      if (newState.applicationsById[action.payload.jobApplicantId]) {
        delete newState.applicationsById[action.payload.jobApplicantId];
      }
      return newState;
    }
    case 'IGNORE_APPLICATION_FAILURE': {
      return state;
    }
    case 'LOAD_JOB_APPLICATIONS_STARTING': {
      const { jobId, statuses } = action.payload.args;
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'jobIdAndStatus',
        jobId,
        true,
      );
    }
    case 'LOAD_JOB_APPLICATIONS_SUCCESS': {
      const {
        args: { jobId, statuses },
        data,
      } = action.payload;
      const applicationsMap = convertArrayToMap(data, (a: FullApplicant) =>
        a.job_applicant_id.toString(),
      );
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'jobIdAndStatus',
        jobId,
        false,
        applicationsMap,
      );
    }
    case 'LOAD_JOB_APPLICATIONS_FAILURE': {
      const { jobId, statuses } = action.payload.args;
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'jobIdAndStatus',
        jobId,
        false,
      );
    }
    case STARTING('LOAD_ORG_APPLICATIONS_BY_STATUS'): {
      const { orgId, statuses } = action.payload.args;
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'orgIdAndStatus',
        orgId,
        true,
      );
    }
    case SUCCESS('LOAD_ORG_APPLICATIONS_BY_STATUS'): {
      const {
        args: { orgId, statuses },
        data,
      } = action.payload;
      const applicationsMap = convertArrayToMap(data, (a: FullApplicant) =>
        String(a.job_applicant_id),
      );
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'orgIdAndStatus',
        orgId,
        false,
        applicationsMap,
      );
    }
    case FAILURE('LOAD_ORG_APPLICATIONS_BY_STATUS'): {
      const { orgId, statuses } = action.payload.args;
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'orgIdAndStatus',
        orgId,
        false,
      );
    }
    case STARTING('LOAD_ACCOUNT_APPLICATIONS_BY_STATUS'): {
      const { accountId, statuses } = action.payload.args;
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'accountIdAndStatus',
        accountId,
        true,
      );
    }
    case SUCCESS('LOAD_ACCOUNT_APPLICATIONS_BY_STATUS'): {
      const {
        args: { accountId, statuses },
        data,
      } = action.payload;
      const applicationsMap = convertArrayToMap(data, (a: FullApplicant) =>
        String(a.job_applicant_id),
      );
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'accountIdAndStatus',
        accountId,
        false,
        applicationsMap,
      );
    }
    case FAILURE('LOAD_ACCOUNT_APPLICATIONS_BY_STATUS'): {
      const { accountId, statuses } = action.payload.args;
      return updateApplicationByParamAndStatuses(
        statuses,
        state,
        'applications',
        'accountIdAndStatus',
        accountId,
        false,
      );
    }
    case STARTING('LOAD_ORG_APPLICATION_COUNTS_BY_STATUS'): {
      const { orgId } = action.payload.args;
      return updateResourceByObjectKey(state, 'applicationCountsByOrgId', orgId, true);
    }
    case SUCCESS('LOAD_ORG_APPLICATION_COUNTS_BY_STATUS'): {
      const {
        args: { orgId },
        data: { countsWithStatus },
      } = action.payload;
      return updateResourceByObjectKey(state, 'applicationCountsByOrgId', orgId, false, {
        [orgId]: countsWithStatus,
      });
    }
    case FAILURE('LOAD_ORG_APPLICATION_COUNTS_BY_STATUS'): {
      const { orgId } = action.payload.args;
      return updateResourceByObjectKey(state, 'applicationCountsByOrgId', orgId, false);
    }
    case 'SET_ORG_APPLICATION_COUNTS_BY_STATUS_DIRTY': {
      const { orgId } = action.payload.args;
      return setStatusByParam(state, 'applicationCounts', 'orgId', orgId, StateStatus.dirty);
    }
    case STARTING('LOAD_JOB_APPLICATION_COUNTS_BY_STATUS'): {
      const { jobId } = action.payload.args;
      return updateResourceByObjectKey(state, 'applicationCountsByJobId', jobId, true);
    }
    case SUCCESS('LOAD_JOB_APPLICATION_COUNTS_BY_STATUS'): {
      const {
        args: { jobId },
        data: { countsWithStatus },
      } = action.payload;
      // Since we are getting counts for a single job, we will only have one jobStatus and can simplify how we store it
      const jobCountsByAppStatus = countsWithStatus.reduce(
        (
          acc: { [appStatus: string]: number },
          { appStatus, count }: { appStatus: string; count: number },
        ) => ({
          ...acc,
          [appStatus]: count,
        }),
        {},
      );

      return updateResourceByObjectKey(state, 'applicationCountsByJobId', jobId, false, {
        [jobId]: jobCountsByAppStatus,
      });
    }
    case FAILURE('LOAD_JOB_APPLICATION_COUNTS_BY_STATUS'): {
      const { jobId } = action.payload.args;
      return updateResourceByObjectKey(state, 'applicationCountsByJobId', jobId, false);
    }
    case 'SET_JOB_APPLICATION_COUNTS_BY_STATUS_DIRTY': {
      const { jobId } = action.payload.args;
      return setStatusByParam(state, 'applicationCounts', 'jobId', jobId, StateStatus.dirty);
    }
    case STARTING('JOB_APPLICATION_LABELS'): {
      return {
        ...state,
        labelStatusByJobId: {
          ...state.labelStatusByJobId,
          [action.payload.args.jobId]: StateStatus.loading,
        },
      };
    }
    case SUCCESS('JOB_APPLICATION_LABELS'): {
      const map = convertArrayToMap(action.payload.data, (a: JobApplicantLabelResponse) =>
        a.job_applicant_id.toString(),
      );
      return {
        ...state,
        labelsByJobApplicantId: { ...state.labelsByJobApplicantId, ...map },
        labelStatusByJobId: {
          ...state.labelStatusByJobId,
          [action.payload.args.jobId]: StateStatus.success,
        },
      };
    }
    case FAILURE('JOB_APPLICATION_LABELS'): {
      return {
        ...state,
        labelStatusByJobId: {
          ...state.labelStatusByJobId,
          [action.payload.args.jobId]: StateStatus.error,
        },
      };
    }
    case 'LOAD_TODOS_SUCCESS': {
      const { jobId, todos } = action.payload;
      const hasMissingApplicants = todos.filter(
        (t: Todo) => !state.applicationsById[t.jobApplicantId],
      ).length;
      return hasMissingApplicants
        ? setStateDirtyByParam(state, 'applications', 'jobId', jobId)
        : state;
    }
    case SUCCESS('ADD_APPLICANT'): {
      const {
        args: { jobId },
        data: { application },
      } = action.payload;
      let newState = setStateDirtyByParam(state, 'applications', 'jobId', jobId);
      newState = setStateDirtyByParam(newState, 'applicationCounts', 'jobId', jobId);
      newState = updateResourceByParam(
        newState,
        'applications',
        'id',
        application.job_applicant_id,
        false,
        { [application.job_applicant_id]: application },
      );

      /*
      Labels and applications are loaded separately, so details will not load
      unless we mark labels as dirty as well.
       */
      const labelStatusByJobId = {
        ...state.labelStatusByJobId,
        [jobId]: { ...state.labelStatusByJobId[jobId], dirty: true },
      };

      return { ...newState, labelStatusByJobId };
    }
    case STARTING('LOAD_APPLICANT_FINALIZING_HIRE_STEPS'): {
      const { jobId, orgId } = action.payload.args;
      return jobId
        ? setStatusByParam(
            state,
            'applicantFinalizingHireSteps',
            'jobId',
            jobId,
            StateStatus.loading,
          )
        : setStatusByParam(
            state,
            'applicantFinalizingHireSteps',
            'orgId',
            orgId,
            StateStatus.loading,
          );
    }
    case SUCCESS('LOAD_APPLICANT_FINALIZING_HIRE_STEPS'): {
      const { jobId, orgId } = action.payload.args;
      const { data } = action.payload;

      const newState = {
        ...state,
        applicantFinalizingHireStepsByJobApplicantId: {
          ...state.applicantFinalizingHireStepsByJobApplicantId,
          ...data,
        },
      };

      return jobId
        ? setStatusByParam(
            newState,
            'applicantFinalizingHireSteps',
            'jobId',
            jobId,
            StateStatus.success,
          )
        : setStatusByParam(
            newState,
            'applicantFinalizingHireSteps',
            'orgId',
            orgId,
            StateStatus.success,
          );
    }
    case FAILURE('LOAD_APPLICANT_FINALIZING_HIRE_STEPS'): {
      const { jobId, orgId } = action.payload.args;
      return jobId
        ? setStatusByParam(state, 'applicantFinalizingHireSteps', 'jobId', jobId, StateStatus.error)
        : setStatusByParam(
            state,
            'applicantFinalizingHireSteps',
            'orgId',
            orgId,
            StateStatus.error,
          );
    }
    case SUCCESS('UPDATE_APPLICANT_FINALIZING_STEP'): {
      const { jobApplicantId, stepId } = action.payload.args;
      const { data } = action.payload;

      const newState: ApplicationState = setStatusDirtyById(
        state,
        'applicationActivityById',
        jobApplicantId,
      );

      const stepsCopy = newState.applicantFinalizingHireStepsByJobApplicantId[jobApplicantId];
      // IF (07/24/20): The only way to update these steps is if they're loaded first. So they'll
      // technically always be present here but need to make typescript happy and check.
      if (!stepsCopy) {
        return newState;
      }
      const index = stepsCopy.findIndex((s) => s.stepId === stepId);
      stepsCopy[index] = data;
      return {
        ...newState,
        applicantFinalizingHireStepsByJobApplicantId: {
          ...newState.applicantFinalizingHireStepsByJobApplicantId,
          [jobApplicantId]: stepsCopy,
        },
      };
    }
    case SUCCESS('SHARE_APPLICANT'): {
      const { jobApplicantId } = action.payload.args;
      return setStatusDirtyById(state, 'applicationActivityById', jobApplicantId);
    }
    case SUCCESS('CREATE_DEMO_APPLICANT'): {
      const { jobId } = action.payload.args;
      let newState = {
        ...state,
        labelStatusByJobId: {
          ...state.labelStatusByJobId,
          [jobId]: { ...state.labelStatusByJobId[jobId], dirty: true },
        },
      };
      action.payload.data.forEach((data: FullApplicant) => {
        const { job_applicant_id: jobApplicantId } = data;
        newState = updateResourceByParam(
          newState,
          'applications',
          'id',
          jobApplicantId.toString(),
          false,
          { [jobApplicantId]: data },
        );
      });
      return newState;
    }
    case SUCCESS('UPDATE_PERSON'): {
      const { personId, update } = action.payload.args;
      const newApplicants = { ...state.applicationsById };
      /*
      Find all applicants with this personId and merge in the update
       */
      const appArray = Object.values(newApplicants);
      const apps = appArray.reduce((newObj, app) => {
        if (app.person_id === personId) {
          newObj[app.job_applicant_id] = { ...app, ...update };
        } else {
          newObj[app.job_applicant_id] = app;
        }
        return newObj;
      }, {} as { [jobApplicantId: string]: FullApplicant });

      return { ...state, applicationsById: apps };
    }
    case SUCCESS('UPDATE_JOB_APPLICANT'): {
      const { jobApplicantId, update } = action.payload.args;
      return setApplicationFields(state, jobApplicantId, update);
    }
    case SUCCESS('SAVE_COMMENT'): {
      const { id: jobApplicantId, profileType } = action.payload.args;
      if (profileType !== 'applicant') {
        return state;
      }
      const existingApplication = state.applicationsById[jobApplicantId];
      state.applicationsById = {
        ...state.applicationsById,
        [jobApplicantId]: {
          ...existingApplication,
          expirationDate: null,
        },
      };
      return setStatusDirtyById(state, 'applicationActivityById', jobApplicantId);
    }
    case SUCCESS('DELETE_COMMENT'): {
      return setStatusDirtyById(state, 'applicationActivityById', action.payload.args.id);
    }
    case STARTING('LOAD_APPLICATION_ACTIVITY'): {
      return updateStatusById(
        state,
        'applicationActivityById',
        true,
        action.payload.args.jobApplicantId,
      );
    }
    case SUCCESS('LOAD_APPLICATION_ACTIVITY'): {
      return updateStatusById(
        state,
        'applicationActivityById',
        false,
        action.payload.args.jobApplicantId,
        action.payload.data,
      );
    }
    case FAILURE('LOAD_APPLICATION_ACTIVITY'): {
      return updateStatusById(
        state,
        'applicationActivityById',
        false,
        action.payload.args.jobApplicantId,
      );
    }
    case 'SET_APPLICATION_ACTIVITY_DIRTY': {
      const { jobApplicantId } = action.payload;
      let newState: ApplicationState = setStatusDirtyById(
        state,
        'applicationActivityById',
        jobApplicantId,
      );
      const thisApplication = newState.applicationsById[jobApplicantId];
      if (thisApplication) {
        Object.values(newState.applicationsById).forEach((application) => {
          if (application.contactId === thisApplication.contactId) {
            newState = setStatusDirtyById(
              newState,
              'applicationActivityById',
              String(application.job_applicant_id),
            );
          }
        });
      }
      return newState;
    }
    case 'SET_DIRTY_ON_TAB_REGAIN_VISIBILITY': {
      let newState = { ...state };
      const applicationActivity = newState.applicationActivityById;
      Object.keys(applicationActivity).forEach((applicantId) => {
        newState = setStatusDirtyById(newState, 'applicationActivityById', applicantId);
      });
      const orgApplicationsState = newState.applicationsStateByOrgIdAndStatus;
      Object.keys(orgApplicationsState).forEach((orgIdAndStatus) => {
        newState = {
          ...newState,
          applicationsStateByOrgIdAndStatus: {
            ...orgApplicationsState,
            [orgIdAndStatus]: StateStatus.dirty,
          },
        };
      });
      const jobApplicationsState = newState.applicationsStateByJobIdAndStatus;
      Object.keys(jobApplicationsState).forEach((jobIdAndStatus) => {
        newState = {
          ...newState,
          applicationsStateByJobIdAndStatus: {
            ...jobApplicationsState,
            [jobIdAndStatus]: StateStatus.dirty,
          },
        };
      });

      return newState;
    }
    case SUCCESS('UPDATE_JOB'): {
      const { job_id: jobId, orgId } = action.payload.data.job;
      const newState = setStateDirtyByParam(state, 'applicantFinalizingHireSteps', 'jobId', jobId);
      return setStateDirtyByParam(newState, 'applicantFinalizingHireSteps', 'orgId', orgId);
    }
    default:
      return state;
  }
};
